/**
 * /verify/monitoring-plan/:id — the public, readable verify page for a signed post-market monitoring plan
 * (V113-6-4; modelled on /verify/insurability-pack). PUBLIC, no sign-in. It reads the same public
 * JSON a machine gets (`GET /verify/monitoring-plan/:id?format=json`): the signed facts only — version,
 * signing date, digest, and whether signature and content check out. No plan content is shown here.
 */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, BoardroomMode, Brand, Button, Card, Spinner } from '@aigovops/design-system';
import { verifyHeadline } from './VerifyInfraPlan';
import { fmtDate } from '../lib/format';
import '../components/programs/strategy-compare.css';
import '../components/programs/infra-inventory.css';
import '../components/programs/plan-view.css';

interface SignedFacts {
  package_id: string; kind: string; version: number | null; signed_at: string | null; content_sha256: string | null; signature_valid: boolean; content_matches_digest: boolean;
  /** Status, not signed: a later signed version of the same system's plan exists. */
  newer_version?: { version: number; signed_at: string } | null;
}

async function fetchFacts(id: string): Promise<SignedFacts | null> {
  const r = await fetch(`/verify/monitoring-plan/${encodeURIComponent(id)}?format=json`, { headers: { accept: 'application/json' } });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error('unavailable');
  return (await r.json()) as SignedFacts;
}

export function VerifyMonitoringPlan() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['verify-monitoring-plan', id], queryFn: () => fetchFacts(id), retry: false });
  const h = q.data ? verifyHeadline({ ...q.data, plan_version_sha256: null }) : null;
  return (
    <BoardroomMode>
      <div className="gp-verify-page">
        <header className="gp-verify-head">
          <div className="gp-verify-inner">
            <Link to="/" className="gp-verify-brand"><Brand variant="glyph" /><Brand variant="wordmark" /></Link>
          </div>
        </header>
        <main className="gp-verify-inner gp-verify-main">
          <h1 className="gp-card-h">Signed post-market monitoring plan</h1>
          <Card>
            {q.isLoading ? <Spinner label="Checking the signature" /> : q.error ? (
              <Alert variant="error"><p>The signature can’t be checked right now.</p><Button compact onClick={() => void q.refetch()}>Try again</Button></Alert>
            ) : !q.data || !h ? (
              <Alert variant="warning"><p>No signed plan has this link. Check that the whole link was copied.</p></Alert>
            ) : (
              <>
                {q.data.newer_version ? (
                  <Alert variant="warning">
                    <p><b>Replaced by version {q.data.newer_version.version}</b>, signed {fmtDate(q.data.newer_version.signed_at)}. Ask the sender for the latest version.</p>
                  </Alert>
                ) : null}
                <p><b>{h.text}</b></p>
                {!h.ok ? <Alert variant="warning"><p>The plan may have been changed after it was signed. Ask the sender for a new link.</p></Alert> : null}
                <details className="gp-cb-why">
                  <summary>Details</summary>
                  <dl className="gp-inv-src">
                    <div><dt>Plan</dt><dd>{q.data.package_id}{q.data.version ? ` · version ${q.data.version}` : ''}</dd></div>
                    <div><dt>Content digest (SHA-256)</dt><dd>{q.data.content_sha256 ?? 'none'}</dd></div>
                    <div><dt>Signature valid</dt><dd>{q.data.signature_valid ? 'Yes' : 'No'}</dd></div>
                    <div><dt>Content matches the digest</dt><dd>{q.data.content_matches_digest ? 'Yes' : 'No'}</dd></div>
                  </dl>
                </details>
              </>
            )}
          </Card>
        </main>
      </div>
    </BoardroomMode>
  );
}
